import * as React from 'react'
import { AlertTriangle, Check, Loader2 } from 'lucide-react'

import {
  useAISettings, useSetAISettings, useTestAIModel, type OllamaConfig,
} from '@/lib/queries'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label, Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/components/ui/toast'
import { cn, formatNumber } from '@/lib/utils'

/**
 * Ollama & LM Studio configuration for AI-assisted Chinese readings.
 *
 * Platform-wide and admin-only: pointing two people's sweeps at two models
 * would make the library disagree with itself, and the readings are shared data
 * rather than anyone's personal preference.
 */
export function AIPanel() {
  const toast = useToast()
  const { data, isLoading } = useAISettings()
  const save = useSetAISettings()
  const test = useTestAIModel()

  const [form, setForm] = React.useState<OllamaConfig>({
    enabled: false, provider: 'ollama', base_url: 'http://localhost:11434', model: '', timeout_seconds: 120,
  })
  const [loaded, setLoaded] = React.useState(false)
  React.useEffect(() => {
    if (data?.config && !loaded) {
      setForm((f) => ({
        ...f,
        ...data.config,
        provider: data.config.provider || 'ollama',
        base_url: data.config.base_url || f.base_url,
      }))
      setLoaded(true)
    }
  }, [data, loaded])

  const currentProvider = form.provider || 'ollama'

  const progress = data?.progress
  const done = (progress?.analysed ?? 0) + (progress?.failed ?? 0)
  const pct = progress?.eligible ? Math.round((done / progress.eligible) * 100) : 0

  async function persist(next: OllamaConfig) {
    try {
      await save.mutateAsync(next)
      // Drop the typed secret once it is stored: leaving it in component state
      // means the next save resends it, and a re-render can put it back on
      // screen in a field the server deliberately never echoes.
      setForm({ ...next, token: '' })
      toast.success('已保存')
    } catch (err) {
      toast.error('保存失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  if (isLoading) return null

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-medium">AI 中文语义</h2>
          <p className="text-xs text-muted-foreground">
            用本地模型（Ollama / LM Studio）补充域名的中文释义。只对含字母的域名调用——
            纯数字由谐音规则处理，模型帮不上忙也费时间。
          </p>
        </div>
        <Switch
          checked={form.enabled}
          onCheckedChange={(v) => {
            const next = { ...form, enabled: v }
            setForm(next)
            void persist(next)
          }}
          aria-label="启用 AI 语义分析"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="服务提供商" hint="选择本地 LLM 引擎">
          <Select
            value={currentProvider}
            onValueChange={(val: 'ollama' | 'lmstudio') => {
              let defaultUrl = form.base_url
              if (val === 'lmstudio' && (form.base_url === 'http://localhost:11434' || !form.base_url)) {
                defaultUrl = 'http://localhost:1234'
              } else if (val === 'ollama' && (form.base_url === 'http://localhost:1234' || !form.base_url)) {
                defaultUrl = 'http://localhost:11434'
              }
              setForm({ ...form, provider: val, base_url: defaultUrl })
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="选择提供商" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ollama">Ollama</SelectItem>
              <SelectItem value="lmstudio">LM Studio (OpenAI API)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field
          label="模型地址"
          hint={currentProvider === 'lmstudio' ? 'LM Studio 服务地址' : 'Ollama 服务地址'}
        >
          <Input
            value={form.base_url}
            onChange={(e) => setForm({ ...form, base_url: e.target.value })}
            placeholder={currentProvider === 'lmstudio' ? 'http://localhost:1234' : 'http://localhost:11434'}
            className="h-9"
          />
        </Field>
        <Field
          label="模型名"
          hint={currentProvider === 'lmstudio' ? '如 qwen2.5-7b-instruct' : '如 qwen2.5:7b'}
        >
          <Input
            value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value })}
            placeholder={currentProvider === 'lmstudio' ? 'qwen2.5-7b-instruct' : 'qwen2.5:7b'}
            className="h-9"
          />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Token"
          hint={data?.has_token
            ? '已保存。留空表示不修改；填 - 表示清除'
            : '网关鉴权用；直连本机 Ollama 通常不需要'}
        >
          <Input
            type="password"
            autoComplete="off"
            value={form.token ?? ''}
            onChange={(e) => setForm({ ...form, token: e.target.value })}
            placeholder={data?.has_token ? '••••••••（已保存）' : '可留空'}
            className="h-9"
          />
        </Field>
        <Field label="单次超时（秒）" hint="CPU 上的本地模型较慢">
          <Input
            type="number" min="10"
            value={form.timeout_seconds}
            onChange={(e) => setForm({ ...form, timeout_seconds: Number(e.target.value) || 120 })}
            className="h-9"
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline" size="sm"
          disabled={test.isPending || !form.base_url || !form.model}
          onClick={() => test.mutate({ ...form, label: 'xie' })}
        >
          {test.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
          测试连接并试读 xie
        </Button>
        <Button size="sm" onClick={() => void persist(form)} disabled={save.isPending}>
          保存
        </Button>
      </div>

      {test.data && <TestResult result={test.data} />}

      {progress && (
        <div className="space-y-3 border-t pt-3">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">分析进度（仅含字母的域名）</span>
              <span className="tabular-nums">
                {formatNumber(done)} / {formatNumber(progress.eligible)}
                {progress.failed > 0 && (
                  <span className="ml-1 text-amber-600">失败 {progress.failed}</span>
                )}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[0.7rem] text-muted-foreground">
              提示词指纹 {data?.prompt_id}。改动提示词或更换模型会使已有结果失效并重新分析。
            </p>
          </div>

          {data?.current?.domain && (
            <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              <span>正在分析域名：<strong className="font-mono text-primary">{data.current.domain}</strong></span>
            </div>
          )}

          {data?.recent && data.recent.length > 0 && (
            <div className="space-y-2 pt-1">
              <h3 className="text-xs font-medium text-muted-foreground">最近分析日志</h3>
              <div className="max-h-56 overflow-y-auto rounded-md border bg-muted/20 text-xs">
                <table className="w-full text-left">
                  <thead className="border-b bg-muted/50 text-[0.7rem] text-muted-foreground">
                    <tr>
                      <th className="px-3 py-1.5 font-medium">域名</th>
                      <th className="px-3 py-1.5 font-medium">AI 分析结果</th>
                      <th className="px-3 py-1.5 font-medium">状态</th>
                      <th className="px-3 py-1.5 text-right font-medium">时间</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y text-[0.75rem]">
                    {data.recent.map((item, idx) => (
                      <tr key={idx} className="hover:bg-muted/30">
                        <td className="px-3 py-1.5 font-mono font-medium">{item.domain}</td>
                        <td className="px-3 py-1.5">
                          {item.reading ? (
                            <span>
                              <span className="font-medium text-foreground">{item.reading}</span>
                              {item.meaning && <span className="ml-1.5 text-muted-foreground">({item.meaning})</span>}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5">
                          {item.error ? (
                            <span className="text-amber-600 truncate max-w-[200px] inline-block">{item.error}</span>
                          ) : (
                            <span className="text-emerald-600 font-medium">成功</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[0.7rem] text-muted-foreground">
                          {new Date(item.at).toLocaleTimeString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function TestResult({
  result,
}: { result: { reachable: boolean; analysed?: boolean; error?: string; reply?: unknown } }) {
  const ok = result.reachable && result.analysed
  return (
    <div className={cn(
      'space-y-1 rounded-md border p-3 text-xs',
      ok ? 'border-primary/40 bg-primary/5' : 'border-amber-500/40 bg-amber-500/10',
    )}>
      <p className="flex items-center gap-1.5 font-medium">
        {ok
          ? <Check className="h-3.5 w-3.5" aria-hidden />
          : <AlertTriangle className="h-3.5 w-3.5" aria-hidden />}
        {/* Three outcomes, three different fixes: a URL problem, a model-name
            problem, or a model too small to follow the prompt. */}
        {!result.reachable ? '无法连接模型服务'
          : !result.analysed ? '服务可达，但模型没能返回有效 JSON'
            : '连接正常，模型返回了结果'}
      </p>
      {result.error && <p className="text-muted-foreground">{result.error}</p>}
      {result.reply != null && (
        <pre className="max-h-56 overflow-auto rounded bg-background/60 p-2 text-[0.7rem]">
          {JSON.stringify(result.reply, null, 2)}
        </pre>
      )}
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
      {hint && <p className="text-[0.7rem] text-muted-foreground">{hint}</p>}
    </div>
  )
}
